import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import DayPicker from 'react-day-picker';
import MomentLocaleUtils from 'react-day-picker/moment';
import 'moment/min/locales';
import { Tooltip } from '../elements-tooltip/tooltip-v2';
import { CustomPortal } from '../elements-portal/portals';
import { Spinner } from 'shared-components/decorators/spinner';
import Calendar from '../../../api/api-calendar';
import LastUpdated from 'shared-components/last-updated/LastUpdated';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';

// Component that displays space is available for chosen date.
const SpaceAvailable = () => {
  return (
    <div className="text-small text-green"><i className="ico icon-confirmed-check" />
      <FormattedMessage id="elements_date_time.space_available.space_available" defaultMessage=" Space is available" />
    </div>
  );
};

// Component that displays space is partially available or fully booked.
const BookingSchedule = ({ schedule }) => {
  const splicedSchedule = [...schedule].splice(0, 2).join(', ');
  const trimmedSchedule = 2 < schedule.length ? `${splicedSchedule}, and ${schedule.length - 2} more time${1 < (schedule.length - 2) ? 's' : ''}` : splicedSchedule;
  return (
    <div className="text-small">
      <FormattedMessage
        id="elements_date_time.booking_schedule.space_available"
        defaultMessage="Space is unavailable from {trimmed}"
        values={{
          trimmed: trimmedSchedule
        }}
      />
    </div>
  );
};

// Component that displays an error message.
const ErrorMessage = () => {
  return (
    <div className="text-danger text-small"><i className="ico icon-attention" />
      <FormattedMessage id="elements_date_time.error_message.something_wrong" defaultMessage=" Something went wrong, try again" />
    </div>
  );
};

class AvailabilityInput extends React.Component {
  constructor (props) {
    super(props);

    this.state = {
      spinner: null,
      isCallPending: false,
      monthAvailability: {},
      additionalInfo: null,
      lastUpdated: ''
    };

    this.anchorClickCB = this.anchorClickCB.bind(this);
    this.onDayPickerOpen = this.onDayPickerOpen.bind(this);
    this.onDayPickerExit = this.onDayPickerExit.bind(this);
    this.onMonthChange = this.onMonthChange.bind(this);
    this.idleApiCall = this.idleApiCall.bind(this);
    this.apiCall = this.apiCall.bind(this);
    this.onClickDay = this.onClickDay.bind(this);
    this.renderBookingSchedule = this.renderBookingSchedule.bind(this);
    this.callToggleShowDate = this.callToggleShowDate.bind(this);
  }

  onDayPickerOpen () {
    const { selectedDay, showBookingSchedule } = this.props;
    // if we don't want to call api and show available dates, showBookingSchedule = false.
    if (!showBookingSchedule) return null;
    // selectDay comes in as moment time, we need to convert to a date string.
    const selectedDaytoDate = selectedDay ? selectedDay.toDate() : new Date();
    this.setState({ isCallPending: true });
    return this.idleApiCall(selectedDaytoDate);
  }

  onMonthChange (date) {
    // cancel spinner and api call when we change the month.
    clearTimeout(this.spinnerStart);
    this.setState({ spinner: null, isCallPending: true, additionalInfo: null }, () => {
      this.idleApiCall(date);
    });
  }

  onDayPickerExit () {
    // cancel spinner and api call when we close calendar.
    clearTimeout(this.spinnerStart);
    this.setState({ spinner: null, additionalInfo: null });
  }

  onClickDay (date, modifiers = {}) {
    if (modifiers.disabled || modifiers.fullyBookedDisabled) { return; }

    const { captureDate, showBookingSchedule } = this.props;

    captureDate(date);

    if (showBookingSchedule) { this.renderBookingSchedule(date); }

    // Listen for double click
    if (!this.delayedClick) {
      this.delayedClick = _.debounce(this.clickFn, 250);
    }
    if (this.clickedOnce) {
      this.delayedClick.cancel();
      this.clickedOnce = false;
      // availtTooltip is the ref name of Tooltip component
      // we are acessing function inside of Tooltip component to close calender
      this.availTooltip.externallyCloseTooltip();
    } else {
      this.delayedClick();
      this.clickedOnce = true;
    }
  }

  clickFn () {
    this.clickedOnce = undefined;
  }

  idleApiCall (date) {
    // We start spinning. Calendar body element is needed for CustomPortal to insert spinner.
    const dayPickerBody = document.getElementsByClassName('DayPicker-Body')[0];
    this.setState({ spinner: dayPickerBody });
    // Wait 500ms before we call the api, in case user changes months or exits calendar.
    this.spinnerStart = setTimeout(() => {
      this.apiCall(date);
      this.setState({ isCallPending: false });
    }, 500);
  }

  apiCall (date) {
    const { spaceId = '', selectedDay, excludeBookingIds } = this.props;
    const month = moment(date).get('month') + 1;
    const year = moment(date).get('year');

    const excludeBookingsQuery = _.isArray(excludeBookingIds) ?
      _.chain(excludeBookingIds).map((bkID, i) => {
        const queryExtension = `exclude_booking_ids=${bkID}`;
        return 0 === i ? `&${queryExtension}` : queryExtension;
      }).join('&').value() : '';

    const query = `bookings/${this.props.isHost ? 'host' : ''}/availability/space/${spaceId}/month?month=${month}&year=${year}${excludeBookingsQuery}`;

    Calendar.readAnonymous(query, (response) => {
      if (response.error || 400 < response.statusCode) {
        return this.setState({ additionalInfo: <ErrorMessage /> });
      }

      const { isCallPending } = this.state;
      if (!isCallPending) {
        const monthAvailability = (response || {}).month_availability || {};
        const lastUpdatedDate = (response || {}).last_updated || '';
        const lastUpdated = <LastUpdated lastUpdatedDate={lastUpdatedDate} /> || '';
        this.setState({ spinner: null, isCallPending: false, monthAvailability, lastUpdated }, () => {
          // if we have a booking_schedule in the cookie (selectedDay), we want the calendar to open
          // with the day already selected on the calendar and booking info rendered below the calendar
          const selectedDaytoDate = selectedDay ? selectedDay.toDate() : null;
          const selectedMonth = moment(selectedDaytoDate).get('month');
          if (selectedDaytoDate && (selectedMonth === month)) {
            return this.renderBookingSchedule(selectedDaytoDate);
          }
          return null;
        });
      }
      return null;
    });
  }

  sortAvailability (monthAvailability, bookedType) {
    if (_.isEmpty(monthAvailability)) return [];

    return _.reduce(monthAvailability, (result, dayHash, day) => {
      if (dayHash[bookedType]) {
        const { year, month } = dayHash;
        result.push(new Date(year, month - 1, day));
      }
      return result;
    }, []);
  }

  anchorClickCB ({ open, showTooltip }) {
    this.callToggleShowDate();

    showTooltip(!open);
  }

  callToggleShowDate () {
    const { toggleShowDate, isBookingForm } = this.props;

    if (isBookingForm && toggleShowDate) {
      toggleShowDate();
    }
  }

  renderBookingSchedule (date) {
    const { monthAvailability } = this.state;
    const day = moment(date).get('date');
    const dayOfSchedule = _.get(monthAvailability, [day, 'booked_times'], []);
    this.setState({ additionalInfo: dayOfSchedule.length ? <BookingSchedule schedule={dayOfSchedule} /> : <SpaceAvailable /> });
  }

  render () {
    const { selectedDay, htmlId, calendarPlacement, isBookingForm, showDate, toggleShowDate, input, isReduxForm, country, intl, placeholder } = this.props;
    const { spinner, monthAvailability, additionalInfo, lastUpdated } = this.state;
    const selectedDaytoDate = selectedDay ? selectedDay.toDate() : null;
    // modifiers adds class names to certain dates when you pass in an array of dates
    // ex: modifiers = { birthday: [new Date(2018, 8, 19), new Date(2018, 8, 30)] }
    // These unique class names are used to style partially booked or fully booked dates.
    const fullyBookedDisabled = this.sortAvailability(monthAvailability, 'is_fully_booked') || [];
    const modifiers = {
      partiallyBooked: this.sortAvailability(monthAvailability, 'is_partially_booked'),
      fullyBookedDisabled
    };

    return (
      <div className="availability-container">
        {
          this.state.spinner ?
            <CustomPortal element={spinner}>
              <div className="calendar-spinner-container">
                <Spinner />
              </div>
            </CustomPortal> : null
        }
        <Tooltip
          ref={ (ref) => { this.availTooltip = ref; }}
          placement={calendarPlacement || 'bottom'}
          tooltipClass="availability-popover"
          anchorClickCB={this.anchorClickCB}
          anchorType="custom"
          openCB={this.onDayPickerOpen}
          exitCB={this.onDayPickerExit}
          showX
          customAnchor={
            isReduxForm ?
              <div className="datepicker__input-container">
                <input
                  {...input}
                  value={input.value ? intl.formatDate(new Date(input.value), { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
                  type="text"
                  id={htmlId}
                  placeholder={ placeholder || intl.formatMessage({ id: 'elements_date_time.availability_input.read_only_start_date', defaultMessage: 'start date' }) }
                  className="datepicker__input"
                  readOnly
                />
              </div>
              :
              <div className="datepicker__input-container">
                <input
                  type="text"
                  id={htmlId}
                  placeholder={ placeholder || this.props.intl.formatMessage({ id: 'elements_date_time.availability_input.start_date', defaultMessage: 'start date' }) }
                  className="datepicker__input"
                  readOnly
                  value={selectedDay ? intl.formatDate(selectedDay, { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
                />
              </div>
          }
          showDate={showDate}
          hideOnClickAnywhere
          shouldUpdatePosition
          isBookingForm={isBookingForm}
          toggleShowDate={toggleShowDate}
        >
          <div className="container-fluid text-center padding-lg-bottom">
            <div className="row">
              <div className="col-xs-12">
                <DayPicker
                  fromMonth={new Date()}
                  onMonthChange={this.onMonthChange}
                  onDayClick={this.onClickDay}
                  className="availability-calendar"
                  month={selectedDaytoDate}
                  selectedDays={selectedDaytoDate}
                  modifiers={modifiers}
                  localeUtils={MomentLocaleUtils}
                  locale={_.get(intl, 'locale', 'en')}
                  disabledDays={[{ before: new Date() }]}
                />
              </div>
            </div>
            {
              lastUpdated &&
              <div className="row last-updated-container text-left">
                <div className="col-xs-12">
                  <span className="last-updated-pill">{lastUpdated}</span>
                </div>
              </div>
            }
            {
              additionalInfo &&
                <div className="row additional-info-row">
                  <div className="col-xs-12">
                    <div className="additional-info text-center margin-lg-bottom">{additionalInfo}</div>
                  </div>
                </div>
            }
          </div>
        </Tooltip>
      </div>
    );
  }
}

BookingSchedule.propTypes = {
  schedule: PropTypes.array
};

AvailabilityInput.propTypes = {
  intl: intlShape.isRequired,
  showBookingSchedule: PropTypes.bool,
  htmlId: PropTypes.string,
  selectedDay: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  spaceId: PropTypes.string,
  captureDate: PropTypes.func,
  toggleShowDate: PropTypes.func,
  calendarPlacement: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  excludeBookingIds: PropTypes.array,
  isBookingForm: PropTypes.bool,
  showDate: PropTypes.bool,
  isHost: PropTypes.bool,
  country: PropTypes.string,
  placeholder: PropTypes.string
};

AvailabilityInput.defaultProps = {
  showBookingSchedule: true
};

export default injectIntl(AvailabilityInput);
